import {
    ApiCommerceStandaloneSnapshotV2ReqSchema,
    ApiCommerceStandaloneSnapshotV2ResSchema,
    ApiCommerceStandalonePresentationCreateV2ReqSchema,
    ApiCommerceStandalonePresentationCreateV2ResSchema,
    type ApiCommerceStandaloneOfferV2,
    type ApiCommerceStandalonePresentationCreateV2Req,
} from "@bridge-applications/pb-types";
import { useSlice } from "@bridge-applications/provider-kit";
import { useCallback, useEffect, useRef } from "react";
import { authenticatedApiCall } from "../helpers/api";
import {
    nextStandaloneBoundary,
    standalonePresentationKey,
} from "../helpers/standaloneOffers";
import i18n from "../i18n/i18n";
import { CommerceApiError } from "./useCommerce";

export function useStandaloneOffers(
    enabled: boolean,
    {
        autoLoad = true,
        watch = autoLoad,
    }: { autoLoad?: boolean; watch?: boolean } = {},
) {
    const { state: auth } = useSlice("authSlice");
    const { state, setSlice } = useSlice("standaloneOffersSlice");
    const requestRef = useRef<AbortController | null>(null);
    const lifecycleRef = useRef<AbortController | null>(null);
    const identityRef = useRef(auth.userId);
    identityRef.current = auth.userId;
    const userId = auth.userId;
    const ownsSnapshot = enabled && state.userId === userId;
    const snapshot = ownsSnapshot ? state.snapshot : null;

    useEffect(() => {
        const lifecycle = new AbortController();
        lifecycleRef.current = lifecycle;
        setSlice((current) =>
            current.userId === userId
                ? {}
                : {
                      userId,
                      snapshot: null,
                      clockOffsetMs: 0,
                      loadStatus: "idle",
                      error: null,
                      shownKeys: [],
                  },
        );
        return () => {
            lifecycle.abort();
            requestRef.current?.abort();
        };
        // Ownership changes cancel all in-flight reads and presentation writes.
    }, [enabled, userId, setSlice]);

    const refresh = useCallback(async () => {
        if (!enabled || !userId) return null;
        requestRef.current?.abort();
        const controller = new AbortController();
        requestRef.current = controller;
        const request = ApiCommerceStandaloneSnapshotV2ReqSchema.parse({});
        const query = new URLSearchParams(request);
        setSlice({ userId, loadStatus: "loading", error: null });
        try {
            const { body } = await authenticatedApiCall(
                `/api/v2/commerce/standalone${query.size ? `?${query}` : ""}`,
                ApiCommerceStandaloneSnapshotV2ResSchema,
                { signal: controller.signal },
            );
            if (controller.signal.aborted || identityRef.current !== userId)
                return null;
            if (body.status === "ERROR") {
                if (body.code === "FEATURE_DISABLED") {
                    setSlice({
                        snapshot: null,
                        loadStatus: "disabled",
                        error: null,
                    });
                    return null;
                }
                throw new CommerceApiError(body.code);
            }
            setSlice({
                snapshot: body.snapshot,
                clockOffsetMs:
                    Date.parse(body.snapshot.observedAt) - Date.now(),
                loadStatus: "ready",
                error: null,
            });
            return body.snapshot;
        } catch (error) {
            if (controller.signal.aborted || identityRef.current !== userId)
                return null;
            setSlice({
                loadStatus: "error",
                error:
                    error instanceof CommerceApiError
                        ? error.message
                        : i18n.t("commerce.errors.unknown"),
            });
            throw error;
        } finally {
            if (requestRef.current === controller) requestRef.current = null;
        }
    }, [enabled, userId, setSlice]);

    const refreshQuietly = useCallback(() => {
        void refresh().catch(() => {});
    }, [refresh]);
    useEffect(() => {
        if (!enabled || !autoLoad) return;
        refreshQuietly();
    }, [enabled, autoLoad, refreshQuietly]);

    useEffect(() => {
        if (!enabled || !watch) return;
        const onResume = () => {
            if (document.visibilityState === "visible") refreshQuietly();
        };
        document.addEventListener("visibilitychange", onResume);
        window.addEventListener("online", onResume);
        return () => {
            document.removeEventListener("visibilitychange", onResume);
            window.removeEventListener("online", onResume);
            requestRef.current?.abort();
        };
    }, [enabled, watch, refreshQuietly]);

    useEffect(() => {
        if (!enabled || !watch || !snapshot || state.loadStatus !== "ready")
            return;
        const now = Date.now() + state.clockOffsetMs;
        const next = nextStandaloneBoundary(snapshot, now);
        if (next === null) return;
        const timer = window.setTimeout(
            refreshQuietly,
            Math.min(2_147_483_647, next - now + 100),
        );
        return () => window.clearTimeout(timer);
    }, [
        enabled,
        watch,
        snapshot,
        state.clockOffsetMs,
        state.loadStatus,
        refreshQuietly,
    ]);

    const recordPresentation = useCallback(
        async (
            offer: ApiCommerceStandaloneOfferV2,
            trigger: ApiCommerceStandalonePresentationCreateV2Req["trigger"],
        ) => {
            const signal = lifecycleRef.current?.signal;
            if (!enabled || !userId || !signal || signal.aborted) return;
            const key = standalonePresentationKey(offer);
            setSlice((current) => ({
                shownKeys: [
                    ...current.shownKeys.filter((value) => value !== key),
                    key,
                ].slice(-128),
            }));
            const request =
                ApiCommerceStandalonePresentationCreateV2ReqSchema.parse({
                    requestId: crypto.randomUUID(),
                    presentationId: crypto.randomUUID(),
                    offerRevisionId: offer.offerRevisionId,
                    trigger,
                });
            const send = () =>
                authenticatedApiCall(
                    "/api/v2/commerce/standalone/presentations",
                    ApiCommerceStandalonePresentationCreateV2ResSchema,
                    { method: "POST", body: JSON.stringify(request), signal },
                );
            try {
                const { body } = await send().catch((error) => {
                    if (signal.aborted) throw error;
                    return send();
                });
                if (body.status === "ERROR")
                    throw new CommerceApiError(body.code);
            } catch (error) {
                // Telemetry must not prevent access to an eligible, server-priced offer.
                if (!signal.aborted)
                    console.error(
                        "[standaloneOffers] presentation failed",
                        error,
                    );
            }
        },
        [enabled, userId, setSlice],
    );

    return {
        userId,
        snapshot,
        clockOffsetMs: ownsSnapshot ? state.clockOffsetMs : 0,
        loadStatus: ownsSnapshot ? state.loadStatus : ("idle" as const),
        error: ownsSnapshot ? state.error : null,
        shownKeys: ownsSnapshot ? state.shownKeys : [],
        refresh,
        refreshQuietly,
        recordPresentation,
    };
}
export type StandaloneOffersController = ReturnType<typeof useStandaloneOffers>;
